import React, { useEffect, useState, useMemo, useRef } from "react";
import { useAuth } from "../../context/authcontext";
import api from "../../Api";
import DashboardComponent from "./DashboardComponent";

function DashboardContainer() {
    const { auth } = useAuth();
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hasFetched, setHasFetched] = useState(false);
    const isMountedRef = useRef(true);
    const fetchInProgressRef = useRef(false);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        // If no userId, set loading to false immediately
        if (!auth.userId) {
            if (isMountedRef.current) {
                setLoading(false);
                setHasFetched(true); // Mark as "fetched" to prevent future attempts
            }
            return;
        }

        // Prevent re-fetching if already fetched for this user
        if (hasFetched) {
            return;
        }

        // Prevent concurrent fetches
        if (fetchInProgressRef.current) {
            return;
        }

        fetchInProgressRef.current = true;

        const fetchDashboardData = async () => {

            if (isMountedRef.current) {
                setLoading(true);
                setError(null);
            } else {
                fetchInProgressRef.current = false;
                return;
            }

            try {
                const response = await api.get("/dashboard/overview");

                if (isMountedRef.current) {
                    console.log("[DASHBOARD] 📥 Backend response received:", {
                        projects: response.data.projects?.length,
                        usersWithInvalidDocs: response.data.usersWithInvalidDocs?.length,
                        userDocuments: Object.keys(response.data.user_documents || {}).length,
                    });

                    if (response.data.usersWithInvalidDocs) {
                        console.log("[DASHBOARD] 👥 Users with invalid docs:", response.data.usersWithInvalidDocs);
                    }

                    setDashboardData(response.data);
                    setError(null);
                    setHasFetched(true); // Mark as successfully fetched
                } else {
                    console.log("[Dashboard] Component unmounted during fetch, ignoring response");
                }
            } catch (err) {
                console.error("[Dashboard] ❌ Fetch error:", err);

                if (isMountedRef.current) {
                    setError("Fout bij laden van dashboard.");
                    // Set empty data even on error so we can show the error state
                    setDashboardData({
                        projects: [],
                        notifications: [],
                        todos: [],
                        timesheets: [],
                        users: [],
                        user_documents: {},
                        totalLaborCosts: 0,
                        totalRegisteredHours: 0,
                        totalRegisteredMeters: 0,
                        totalExtraMaterialCosts: 0,
                        usersWithInvalidDocs: []
                    });
                    setHasFetched(true); // Mark as fetched even on error to prevent retry loop
                }
            } finally {
                fetchInProgressRef.current = false;
                if (isMountedRef.current) {
                    setLoading(false);
                } else {
                    console.log("[Dashboard] Component unmounted, not setting loading");
                }
            }
        };

        fetchDashboardData().then(() => {
        }).catch((err) => {
            console.error("[Dashboard] fetchDashboardData promise rejected:", err);
        });
    }, [auth.userId, hasFetched]); // Add hasFetched to dependencies

    // Memoize the data to prevent unnecessary re-renders
    const memoizedData = useMemo(() => {
        if (!dashboardData) {
            return {
                projects: [],
                notifications: [],
                todos: [],
                timesheets: [],
                users: [],
                userDocuments: {},
                totalLaborCosts: 0,
                totalRegisteredHours: 0,
                totalRegisteredMeters: 0,
                totalExtraMaterialCosts: 0,
                usersWithInvalidDocs: []
            };
        }
        return {
            projects: dashboardData.projects || [],
            notifications: dashboardData.notifications || [],
            todos: dashboardData.todos || [],
            timesheets: dashboardData.timesheets || [],
            users: dashboardData.usersWithInvalidDocs || [],
            userDocuments: dashboardData.user_documents || {},
            totalLaborCosts: dashboardData.totalLaborCosts || 0,
            totalRegisteredHours: dashboardData.totalRegisteredHours || 0,
            totalRegisteredMeters: dashboardData.totalRegisteredMeters || 0,
            totalExtraMaterialCosts: dashboardData.totalExtraMaterialCosts || 0,
            usersWithInvalidDocs: dashboardData.usersWithInvalidDocs || []
        };
    }, [dashboardData]);

    // Show loading state only while fetching
    if (loading) {
        return <DashboardComponent
            auth={auth}
            projects={[]}
            notifications={[]}
            todos={[]}
            timesheets={[]}
            users={[]}
            userDocuments={{}}
            totalLaborCosts={0}
            totalRegisteredHours={0}
            totalRegisteredMeters={0}
            totalExtraMaterialCosts={0}
            usersWithInvalidDocs={[]}
            loading={true}
            error={null}
        />;
    }

    // Show dashboard with data (or empty data if there was an error)
    return (
        <DashboardComponent
            auth={auth}
            projects={memoizedData.projects}
            notifications={memoizedData.notifications}
            todos={memoizedData.todos}
            timesheets={memoizedData.timesheets}
            users={memoizedData.users}
            userDocuments={memoizedData.userDocuments}
            totalLaborCosts={memoizedData.totalLaborCosts}
            totalRegisteredHours={memoizedData.totalRegisteredHours}
            totalRegisteredMeters={memoizedData.totalRegisteredMeters}
            totalExtraMaterialCosts={memoizedData.totalExtraMaterialCosts}
            usersWithInvalidDocs={memoizedData.usersWithInvalidDocs}
            loading={false}
            error={error}
        />
    );
}

export default DashboardContainer;
